import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Search, Globe, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { getCountries } from 'react-phone-number-input';
import { consentTexts, ALL_COUNTRIES, getConsentKeysForCountry, getRegionLabel, getNoticeTitleSuffix } from './GDPRConsentData';
import IndiaConsentForm from 'src/components/consent/IndiaConsentForm';
import { getIndiaConsentsForSurface, areRequiredConsentsGiven } from 'src/config/consent/indiaConsentRegistry';
import { buildConsentArtefact } from 'src/config/consent/consentArtefact';
import { enqueueConsent, CONSENT_STRICT_MODE } from 'src/config/consent/consentOutbox';
import apiService from 'src/shared/Services/apiService';
import type { ConsentArtefactRequest } from 'src/shared/Services/apiService/ConsentTypes';
import { ISO_TO_COUNTRY } from 'src/config/countryConfig';

interface Props {
  onComplete: () => void;
  triggerSubmit: (fn: () => void) => void;
  onStatusChange?: (ready: boolean) => void;
  onCountryChange?: (country: string) => void;
  initialCountry?: string;
  editableCountry?: boolean;
  editableConsent?: boolean;
  detectionSource?: string;
}

/** How long to wait for `consents/record` before falling back to the outbox. */
const CONSENT_RECORD_TIMEOUT_MS = 8000;

const _regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const NAME_TO_ISO: Record<string, string> = Object.fromEntries(
  getCountries().map(code => [_regionNames.of(code) || code, code])
);
const countryFlag = (name: string): string => {
  const iso = NAME_TO_ISO[name];
  if (!iso) return '🌐';
  return iso.toUpperCase().replace(/./g, ch =>
    String.fromCodePoint(0x1F1E6 - 65 + ch.charCodeAt(0)));
};

export default function GDPRConsent({
  onComplete,
  triggerSubmit,
  onStatusChange,
  onCountryChange,
  initialCountry,
  editableCountry = true,
  editableConsent = true,
  detectionSource,
}: Props) {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [openNotice, setOpenNotice] = useState<string | null>(null);
  const ddRef = useRef<HTMLDivElement>(null);

  const isIndia = selectedCountry === ISO_TO_COUNTRY.IN;
  const [indiaState, setIndiaState] = useState<Record<string, boolean>>({});
  const indiaReady = useMemo(
    () => areRequiredConsentsGiven(indiaState, 'onboarding'),
    [indiaState],
  );

  const filtered = useMemo(() => {
    if (!countrySearch) return ALL_COUNTRIES;
    const q = countrySearch.toLowerCase();
    return ALL_COUNTRIES.filter((c: string) => c.toLowerCase().includes(q));
  }, [countrySearch]);

  const consentKeys = useMemo(
    () => selectedCountry ? getConsentKeysForCountry(selectedCountry) : [],
    [selectedCountry],
  );

  useEffect(() => {
    const ok = isIndia
      ? indiaReady
      : !!selectedCountry && consentKeys.length > 0 && consentKeys.every((k: string) => checked[k]);
    onStatusChange?.(ok);
  }, [isIndia, indiaReady, selectedCountry, checked, consentKeys, onStatusChange]);

  useEffect(() => { if (selectedCountry) onCountryChange?.(selectedCountry); }, [selectedCountry, onCountryChange]);

  // Apply auto-detected initialCountry on mount (only if not already set)
  useEffect(() => {
    if (initialCountry && !selectedCountry) {
      setSelectedCountry(initialCountry);
      onCountryChange?.(initialCountry);
    }
  }, [initialCountry]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ddRef.current && !ddRef.current.contains(e.target as Node)) setIsDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Recording is asynchronous and the Continue button is not disabled while it
  // runs, so guard against a second click queueing a duplicate artefact.
  const recordingRef = useRef(false);

  /** India only — build the artefact, record it, then advance. */
  const recordIndiaConsent = useCallback(async () => {
    let artefact: ConsentArtefactRequest;
    try {
      artefact = await buildConsentArtefact({
        decisions: indiaState,
        items: getIndiaConsentsForSurface('onboarding'),
        method: 'patient_explicit',
        surface: 'onboarding',
      });
    } catch {
      toast.error('We could not prepare your consent record. Please try again.');
      return;
    }

    try {
      // No global axios timeout is configured, and step 0 does not raise the
      // stepper's `loading` flag — so an unresponsive endpoint would leave
      // Continue looking dead. Bound the wait ourselves.
      await Promise.race([
        apiService.recordConsent(artefact),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('consent-record-timeout')), CONSENT_RECORD_TIMEOUT_MS)),
      ]);
    } catch {
      // The endpoint is not live yet, or did not answer in time. Queue the
      // artefact rather than losing it and let the patient continue — unless
      // strict mode is on, in which case an unrecorded consent must block.
      if (CONSENT_STRICT_MODE) {
        toast.error('We could not save your consent, so we cannot continue. Please try again.');
        return;
      }
      enqueueConsent(artefact);
      console.warn('[consent] consents/record unavailable — artefact queued locally.');
    }

    onComplete();
  }, [indiaState, onComplete]);

  const submit = useCallback(async () => {
    if (!selectedCountry) { toast.error('Please select your country.'); return; }

    if (isIndia) {
      if (!areRequiredConsentsGiven(indiaState, 'onboarding')) {
        toast.error('Please accept the required items to continue.');
        return;
      }
      if (recordingRef.current) return;
      recordingRef.current = true;
      try {
        await recordIndiaConsent();
      } finally {
        recordingRef.current = false;
      }
      return;
    }

    if (!consentKeys.every((k: string) => checked[k])) { toast.error('Please accept all consent items.'); return; }
    onComplete();
  }, [selectedCountry, isIndia, indiaState, checked, consentKeys, onComplete, recordIndiaConsent]);

  const submitRef = useRef(submit);
  const reg = useRef(false);
  useEffect(() => { submitRef.current = submit; }, [submit]);
  useEffect(() => { if (triggerSubmit && !reg.current) { triggerSubmit(() => submitRef.current()); reg.current = true; } }, [triggerSubmit]);

  return (
    <div className="w-full mx-auto">
      <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-brand-100 bg-brand-50/80 px-3 py-2.5 text-sm dark:border-brand-400/35 dark:bg-brand-500/[0.10]">
        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-brand-500/12">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-brand-500 dark:text-brand-300">
            <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
          </svg>
        </div>
        <p className="leading-6 text-slate-600 dark:text-slate-200">
          <span className="font-semibold text-slate-900 dark:text-slate-100">Important:</span> MoreThanOne is operated by Symbian Health. You are now being transferred to the secure Symbian Health patient data portal, where your information is handled in accordance with applicable data protection regulations.
        </p>
      </div>

      {/* <div className="mb-4">
        <div className="mb-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500/15 to-brand-600/10">
              <Globe size={15} className="text-brand-600 dark:text-brand-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                {editableCountry ? 'Select Your Country' : 'Your Country'}
              </h3>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {editableCountry
                  ? 'We need your country to show the correct data protection terms that apply to you.'
                  : 'Your data protection terms have been automatically configured for your country.'}
              </p>
            </div>
          </div>
        </div>

        {!editableCountry ? (
          <div className="flex min-h-[44px] w-full items-center gap-3 rounded-xl border border-brand-500/35 bg-white px-3.5 py-2.5 dark:border-brand-400/60 dark:bg-[#1E3356]">
            <span className="text-lg leading-none">{countryFlag(selectedCountry)}</span>
            <span className="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedCountry}</span>
            <CheckCircle2 size={16} className="shrink-0 text-brand-500 dark:text-brand-300" />
          </div>
        ) : (
          <div className="relative" ref={ddRef}>
            <button
              type="button"
              id="country-selector"
              onClick={() => setIsDropdownOpen(o => !o)}
              className={[
                'flex min-h-[44px] w-full items-center justify-between rounded-xl border px-3.5 py-2.5 text-left transition-all duration-200',
                'bg-white text-slate-800 dark:bg-[#1E3356] dark:text-slate-100',
                isDropdownOpen
                  ? 'border-brand-500 ring-2 ring-brand-500/15 shadow-[0_4px_14px_-8px_rgba(3,93,184,0.45)] dark:ring-brand-400/25'
                  : selectedCountry
                    ? 'border-brand-500/35 hover:border-brand-500/60 dark:border-brand-400/60 dark:hover:border-brand-400'
                    : 'border-slate-300 hover:border-brand-400 dark:border-white/30 dark:hover:border-white/50',
              ].join(' ')}
            >
              {selectedCountry ? (
                <span className="flex items-center gap-2.5">
                  <span className="text-lg leading-none">{countryFlag(selectedCountry)}</span>
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{selectedCountry}</span>
                </span>
              ) : (
                <span className="text-sm font-medium text-slate-400 dark:text-white/55">Choose your country…</span>
              )}
              <svg className={`h-4 w-4 text-slate-500 transition-transform dark:text-slate-400 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-xl border bg-white border-slate-200 shadow-[0_14px_34px_-18px_rgba(3,45,105,0.65),0_4px_14px_-8px_rgba(15,23,42,0.30)] dark:bg-[#243F6A] dark:border-brand-400/50 dark:shadow-[0_24px_70px_-20px_rgba(0,0,0,0.90)]">
                <div className="p-2 border-b border-slate-100 dark:border-white/20">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-300" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search countries…"
                      value={countrySearch}
                      onChange={e => setCountrySearch(e.target.value)}
                      className="w-full rounded-lg border bg-slate-50 py-2 pl-8 pr-3 text-sm font-medium text-slate-800 outline-none transition-colors border-slate-300 placeholder:text-slate-400 focus:border-brand-500 dark:bg-[#1E3356] dark:border-white/30 dark:text-slate-100 dark:placeholder:text-white/40 dark:focus:border-brand-400"
                    />
                  </div>
                </div>
                <div className="max-h-44 overflow-y-auto scrollbar-hide divide-y divide-slate-100 dark:divide-white/10">
                  {filtered.length === 0 ? (
                    <p className="py-5 text-center text-sm text-slate-500 dark:text-slate-300">No countries found</p>
                  ) : (
                    filtered.map((c: string) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => { setSelectedCountry(c); setIsDropdownOpen(false); setCountrySearch(''); setChecked({}); }}
                        className={[
                          'flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-sm font-medium transition-colors',
                          c === selectedCountry
                            ? 'bg-brand-50 text-brand-700 font-semibold dark:bg-brand-500/35 dark:text-white'
                            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-white/[0.10]',
                        ].join(' ')}
                      >
                        <span className="text-base leading-none">{countryFlag(c)}</span>
                        <span className="flex-1">{c}</span>
                        {c === selectedCountry && <CheckCircle2 size={15} className="text-brand-500 dark:text-brand-200" />}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div> */}

      {/* India — itemised DPDP consent (s.6 specific, Rule 3 itemised notice) */}
      {isIndia && (
        <IndiaConsentForm
          surface="onboarding"
          hideHeading
          onChange={setIndiaState}
        />
      )}

      {!isIndia && selectedCountry && consentKeys.length > 0 && (
        <div className="space-y-2.5">
          {consentKeys.map((key: string) => {
            const s = (consentTexts as any)[key];
            if (!s) return null;
            const on = checked[key] || false;
            return (
              <div key={key}
                className={[
                  'group cursor-pointer rounded-xl border p-3.5 transition-all duration-200',
                  on
                    ? 'border-brand-500 bg-brand-50 shadow-[0_6px_18px_-12px_rgba(3,93,184,0.70)] dark:border-brand-400 dark:bg-[#0F1E38]'
                    : 'border-slate-200 bg-white shadow-sm hover:border-brand-300 hover:shadow-md dark:border-white/25 dark:bg-[#162338] dark:hover:border-white/45 dark:hover:bg-[#1A2D4A]',
                ].join(' ')}
                onClick={() => setChecked((p) => ({ ...p, [key]: !on }))}
              >
                <div className="flex items-start gap-3">
                  <div className={[
                    'mt-0.5 flex h-6 w-6 min-w-6 items-center justify-center rounded-lg border transition-all duration-200',
                    on
                      ? 'border-brand-500 bg-gradient-to-br from-brand-400 to-brand-600 shadow-[0_0_0_3px_rgba(3,93,184,0.14)]'
                      : 'border-slate-300 bg-white group-hover:border-brand-400 dark:border-white/40 dark:bg-[#0C1828] dark:group-hover:border-brand-400',
                  ].join(' ')}>
                    {on && (
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M2.5 6.5L5.5 9.5L10.5 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <h4 className={`text-sm font-bold ${on ? 'text-brand-700 dark:text-brand-300' : 'text-slate-800 dark:text-slate-100'}`}>{selectedCountry}</h4>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:bg-white/10 dark:text-slate-300">
                        {getRegionLabel(selectedCountry)}
                      </span>
                    </div>
                    <p className="text-sm leading-6 text-slate-600 dark:text-slate-200">
                      {s.checkboxLabel}{' '}
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenNotice(key); }}
                        className="font-semibold underline underline-offset-2 text-brand-500 transition-colors hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200">
                        View full notice →
                      </button>
                    </p>
                    {s.whyText && <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{s.whyText}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openNotice && (() => {
        const s = (consentTexts as any)[openNotice];
        if (!s) return null;
        const lines = s.fullText.split('\n').filter((l: string) => l.trim());
        const modal = (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setOpenNotice(null)}>
            <div className="rounded-2xl overflow-hidden w-full max-w-2xl max-h-[85dvh] flex flex-col mx-4 bg-white border border-slate-200 shadow-[0_24px_64px_-12px_rgba(3,93,184,0.18),0_8px_32px_-8px_rgba(0,0,0,0.10)] dark:bg-[#162338] dark:border-white/25 dark:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.7)]"
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3.5 sm:py-4 border-b border-slate-100 dark:border-white/15">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-brand-500">
                    <ShieldCheck size={15} className="text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{selectedCountry} – {getNoticeTitleSuffix(selectedCountry)}</h3>
                </div>
                <button onClick={() => setOpenNotice(null)}
                  className="p-2 rounded-lg transition-colors text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-white/[0.08]">
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-y-auto p-6 text-sm leading-7 text-slate-600 dark:text-slate-200">
                {lines.map((line: string, i: number) => {
                  const t = line.trim();
                  if (/^(Controller|Purposes|Recipients|International|Retention|Your Rights|Children|Statement|Voluntary)/i.test(t))
                    return <h4 key={i} className="mt-5 mb-2 text-sm font-bold text-slate-800 dark:text-slate-100">{t}</h4>;
                  if (t.startsWith('•'))
                    return <p key={i} className="ml-4 mb-1 flex gap-2"><span className="text-brand-500">•</span>{t.slice(1).trim()}</p>;
                  return <p key={i} className="mb-2">{t}</p>;
                })}
              </div>
            </div>
          </div>
        );
        return ReactDOM.createPortal(modal, document.body);
      })()}
    </div>
  );
}
